import { DragEvent, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";

interface Props {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export const MAX_DIMENSION = 1000;
export const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8 MB per file

const ACCEPTED = ["image/png", "image/jpeg"];

function readDimensions(file: File): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("無法讀取圖片"));
    };
    img.src = url;
  });
}

export function PngDropzone({ onFiles, disabled }: Props) {
  const [over, setOver] = useState(false);
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept = async (list: FileList | File[]) => {
    setChecking(true);
    const arr = Array.from(list);
    const good: File[] = [];
    for (const f of arr) {
      const isImg = ACCEPTED.includes(f.type);
      if (!isImg) {
        toast.error(`${f.name}：僅接受 PNG 或 JPG`);
        continue;
      }
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name}：檔案超過 8 MB`);
        continue;
      }
      try {
        const { w, h } = await readDimensions(f);
        if (w > MAX_DIMENSION || h > MAX_DIMENSION) {
          toast.error(`${f.name}：尺寸 ${w}×${h}，超過 ${MAX_DIMENSION}×${MAX_DIMENSION}`);
          continue;
        }
      } catch {
        toast.error(`${f.name}：無法讀取尺寸`);
        continue;
      }
      good.push(f);
    }
    setChecking(false);
    if (good.length) onFiles(good);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    void accept(e.dataTransfer.files);
  };

  return (
    <div
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
        over ? "border-accent bg-accent/10" : "border-border bg-muted/30 hover:bg-muted/50"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
      <p className="text-sm font-medium">
        {checking ? "檢查中…" : "拖曳圖片到這裡，或點擊選檔"}
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        支援 PNG / JPG · 單張最大 {MAX_DIMENSION}×{MAX_DIMENSION} · 檔案 ≤ 8 MB
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) void accept(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
